import * as _ from "lodash";
import './RollContractsModal.scss';
import { formatDecimalPlace, formatUTCDate, getNextDay, getPreviousDayForGivenDate, pluralize } from 'common/formatters';
import NumberEditor from 'common/ui/NumberEditor';
import TypedSelect from 'common/ui/TypedSelect';
import Commodity from 'domain/commodity';
import DeliveryPeriod, { getDeliveryPeriodDisplayName } from 'domain/deliveryPeriod';
import FuturesContract from 'domain/futuresContract';
import deliveryPeriodService from 'features/deliveryPeriods/deliveryPeriod.service';
import futuresContractsService from 'features/futuresContracts/futuresContracts.service';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import loggingService from "common/logging/loggingService";
import ValidationError from "common/validationError";
import ErrorList from "common/ui/ErrorList";
import Contract, { ContractType, getContractTypeName } from "domain/contract";
import contractService, { RollContractsRequest } from "../contractService";
import { DisplayContractsEnum } from "../displayContractsEnum";
import moment from "moment";
import { getSecondLastBusinessDayOfLastMonth } from "features/offers/helpers";
import useStoreSelector from "common/redux/useStoreSelector";
import useCancellationToken from "common/ui/useCancellationToken";
import { withCancellationToken } from "common/http/httpService";
import { useFcmAccountsLoader } from "features/fcmAccounts/useFcmAccounts";
import FcmAccount from "features/fcmAccounts/fcmAccount";
import { ContractRow } from "./contractRow";
import RollParameters from "./rollParameters";
import RollHtaTable from "./RollHtaTable";
import RollBasisTable from "./RollBasisTable";
import RollTableProps from "./rollTableProps";
import { calculateNumberOfFullContracts } from "features/offers/contractCalculator";
import StandardGrid from "common/agGrid/StandardGrid";
import { AgGridColumn } from "@ag-grid-community/react";
import { getPositionNameForPositionGroup } from "domain/positionGroup";
import { PositionSetting } from "domain/positionSetting";
import { usePositionSettingsForCommodity } from "features/positions/usePositionSettings";
import getLotsToHedge from "features/positions/positionHedgeCalculator";
import { ValueFormatterParams, ValueGetterParams } from "@ag-grid-community/all-modules";
import RenderParameters from "common/agGrid/renderParameters";
import { useStandardJourney } from "common/ui/useJourney";
import FormValidation, { useFormValidation } from "common/ui/FormValidation";
import DetailedList, { DetailRow } from "common/ui/DetailedList";
import unitHelper from "domain/unitHelper";
import { UnitConversionRate } from "domain/unit";
import useUnitConversionRates from "features/settings/commodities/useUnitConversionRates";
import { Currency } from "domain/currency";
import JourneyModal from "common/ui/JourneyModal";
import UiCustomization from "domain/uiCustomization";
import { ErpSystem } from "domain/erpSystem";
import useUiCustomization from "features/merchants/useUiCustomization";
import contractTypeHelper from "domain/contractTypeHelper";
import { sumArrayBy } from "common/decimal";
import { getOfferContractOrderTypeName, OrderType } from "domain/orderType";
import { AllOrderExpirationTypes, getOrderExpirationTypeName, OrderExpirationType } from "domain/orderExpirationType";
import { StrategyType } from "features/positions/strategyType";
import useMerchantFeature from "features/merchants/useMerchantFeature";
import { MerchantFeature } from "domain/merchant";
import useActiveMerchant from "features/merchants/useActiveMerchant";
import { useFeesLoader } from "features/merchants/useFees";
import { listAllowedFees } from "features/settings/general/fees/feeHelpers";
import { Fee, FeeType, getFeeValue } from "domain/fee";
import { useCustomerWillSellText } from "common/formatters/useCustomerWillSellText";

interface RollContractsModalProps {
    contractType: ContractType;
    contract?: Contract;
    onClose(): void;
    onContractsRolled?(): void;
}

interface ContractFilter {
    commodity: Commodity;
    customerWillSell?: boolean;
}

const defaultRollParameters: RollParameters = { deliveryPeriod: null, futuresContract: null }

type SelectedSpreadDetails = { contract: FuturesContract, multiplier: number };

const RollContractsModal: React.FC<RollContractsModalProps> = props => {
    const customerWillSellText = useCustomerWillSellText();
    const cxt = useCancellationToken();
    const commodities = useStoreSelector(m => m.myCommodities);
    const hasErpIntegration = useStoreSelector(m => m.userState.activeMerchant.hasErpIntegration)
    const isHta = props.contractType === ContractType.Hta;

    const [filter, setFilter] = useState<ContractFilter>({
        commodity: props.contract?.commodity ?? commodities[0],
        customerWillSell: props.contract?.customerWillSell ?? true
    });
    const [deliveryPeriods, setDeliveryPeriods] = useState<DeliveryPeriod[]>([]);
    const [futuresContracts, setFuturesContracts] = useState<FuturesContract[]>([]);
    const [spreadFuturesContracts, setSpreadFuturesContracts] = useState<FuturesContract[]>([]);
    const [selectedSpread, setSelectedSpread] = useState<SelectedSpreadDetails>({ contract: null, multiplier: 1 });
    const [allContracts, setAllContracts] = useState<ContractRow[]>([]);
    const [rollFromDeliveryPeriods, setRollFromDeliveryPeriods] = useState<DeliveryPeriod[]>([]);
    const [rollFrom, setRollFrom] = useState<RollParameters>({ ...defaultRollParameters });
    const [rollTo, setRollTo] = useState<RollParameters>({ ...defaultRollParameters });
    const [spread, setSpread] = useState<number>(null);
    const [orderType, setOrderType] = useState(isHta ? OrderType.Market : null);
    const [expirationInfo, setExpirationInfo] = useState<{ type: OrderExpirationType, date: Date }>(null);
    const [rollFeeValue, setRollFeeValue] = useState(0);
    const [selectedFinalPricingDate, setSelectedFinalPricingDate] = useState<Date>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [lotsByPosition, setLotsByPosition] = useState<LotsByPosition[]>([]);
    const [useUnappliedBalance, setUseUnappliedBalance] = useState(null);
    const [rollFee, setRollFee] = useState<Fee>(null);

    const fcmAccountsLoader = useFcmAccountsLoader();
    const positionSettings = usePositionSettingsForCommodity(filter.commodity.id);
    const unitConversionRates = useUnitConversionRates();
    const uiCustomization = useUiCustomization();
    const activeMerchant = useActiveMerchant();
    const fees = useFeesLoader();

    const filteredContracts = getVisibleRows(allContracts, rollFrom.deliveryPeriod, rollFrom.futuresContract);

    const isSingleContract = props.contract != null;

    const formValidator = useFormValidation();
    const baseJourney = useStandardJourney(onSave, formValidator);

    const allowPartialRoll = useMerchantFeature(MerchantFeature.PartialRollContract)

    const isMissingRollFee = rollFee == null;

    function goBack() {
        setErrors([]);
        baseJourney.goBack();
    }

    const journey = {
        ...baseJourney,
        goBack
    };

    useEffect(() => {
        if (filter.commodity == null || !isLoaded()) return;

        populateCommodityData(filter.commodity);
        populateContracts(filter.commodity, filter.customerWillSell);
    }, [fcmAccountsLoader.isLoaded, positionSettings.length]);

    useEffect(() => {
        if (!fees.isLoaded || rollTo.deliveryPeriod == null) return;

        const fee = listAllowedFees(fees.data, filter.customerWillSell, filter.commodity, props.contractType)
            .find(m => m.type === FeeType.Roll);

        setRollFee(fee);
        setRollFeeValue(fee == null ? 0 : getFeeValue(fee, rollTo.deliveryPeriod.start) ?? 0);

    }, [fees.isLoaded, filter.customerWillSell, filter.commodity, rollTo.deliveryPeriod]);

    function selectAll(selected: boolean) {
        selectRows(filteredContracts, selected, false);
    }

    const rollToFuturesContracts = futuresContracts.filter(m => m.expiration !== rollFrom.futuresContract?.expiration);

    async function populateCommodityData(commodity: Commodity) {
        if (isSingleContract) {
            setRollFrom({ deliveryPeriod: props.contract.deliveryPeriod, futuresContract: props.contract.futuresContract });
        }
        else {
            setRollFrom({ ...defaultRollParameters });
        }
        setRollTo({ ...defaultRollParameters });
        withCancellationToken(deliveryPeriodService, cxt).listDeliveryPeriods(commodity.id)
            .then(result => {
                setDeliveryPeriods(result.deliveryPeriods);
            });
        withCancellationToken(futuresContractsService, cxt).listFuturesContracts(commodity.id)
            .then(result => setFuturesContracts(result));

        withCancellationToken(futuresContractsService, cxt).listFuturesContracts(commodity.id, false, StrategyType.Spread)
            .then(result => setSpreadFuturesContracts(result));
    }

    async function populateContracts(commodity: Commodity, customerWillSell: boolean) {
        if (filter.commodity == null) return;

        if (isSingleContract) {
            const rowData = [{ ...props.contract, isSelected: true, rollQuantity: props.contract.remainingQuantity, hedgeCount: getHedgeResult(props.contract) }];
            setAllContracts(rowData);
            setRollFromDeliveryPeriods([props.contract.deliveryPeriod]);
            setLotsByPosition(calculateLots(rowData));
            return;
        }
        setAllContracts([]);

        const result = await withCancellationToken(contractService, cxt).listContracts({
            commodities: [commodity],
            contractType: props.contractType,
            willSell: customerWillSell,
            displayContracts: DisplayContractsEnum.withNonZeroQuantities
        });
        const contractsToShow = result.results
            .filter(m => !m.isReadOnly && !m.isRollInProgress);
        const rollFromOptions = _.orderBy(
            _.uniqBy(contractsToShow.map(m => m.deliveryPeriod).filter(m => m != null), m => m.id),
            m => m.start
        );
        setRollFromDeliveryPeriods(rollFromOptions);
        let newRollFrom = rollFrom;
        if (rollFromOptions.length === 0) {
            newRollFrom = { ...defaultRollParameters };
        }
        else if (!rollFromOptions.some(m => m.id === rollFrom.deliveryPeriod?.id)) {
            const fc = isHta ? rollFromOptions[0].contract : null
            newRollFrom = { deliveryPeriod: rollFromOptions[0], futuresContract: fc };
        }
        setRollFrom(newRollFrom);

        const all = contractsToShow.map(m => ({
            ...m,
            isSelected: m.deliveryPeriod?.id === newRollFrom?.deliveryPeriod.id,
            rollQuantity: m.remainingQuantity,
            hedgeCount: getHedgeResult(m)
        }));
        setAllContracts(all);
        setLotsByPosition(calculateLots(all.filter(m => m.isSelected)));
    }

    function getHedgeResult(contract: Contract): number {
        if (!contract.commodity || contract.remainingQuantity < filter.commodity.contractSize) return 0;

        return calculateNumberOfFullContracts(contract.commodity, contract.remainingQuantity, false);
    }

    function getVisibleRows(contracts: ContractRow[], deliveryPeriod: DeliveryPeriod, futuresContract: FuturesContract = null): ContractRow[] {
        return contracts.filter(m => m.deliveryPeriod?.id === deliveryPeriod?.id &&
            (futuresContract == null || m.deliveryPeriod?.contract?.symbol === futuresContract.symbol)
        );
    }

    function onRollFromChanged(roll: Partial<RollParameters>) {
        setRollTo({ ...defaultRollParameters });
        setSpread(0);
        setSelectedFinalPricingDate(null);
        const newVal = { ...rollFrom, ...roll };
        setRollFrom(newVal);
        selectRows(getVisibleRows(allContracts, newVal.deliveryPeriod, newVal.futuresContract), true, true)
    }

    function onRollToChanged(roll: Partial<RollParameters>) {
        const newVal = { ...rollTo, ...roll };

        setRollTo(newVal);

        let newSpread = 0;

        //If we're rolling back, get the inverse of the spread
        //For example, rolling from CH26 to CZ25 would use CZ25-CH26 price * -1
        let start, end;
        const spreadDetails: SelectedSpreadDetails = { contract: null, multiplier: 1 }
        if (rollFrom.futuresContract.expiration < newVal.futuresContract?.expiration) {
            start = rollFrom.futuresContract;
            end = newVal.futuresContract;
        }
        else {
            start = newVal.futuresContract;
            end = rollFrom.futuresContract;
            spreadDetails.multiplier = -1;
        }

        if (start != null && end != null) {
            const spreadContract = spreadFuturesContracts.find(m =>
                m.maturityMonth === start.maturityMonth && m.maturityYear === start.maturityYear &&
                m.spreadEndMonth === end.maturityMonth && m.spreadEndYear === end.maturityYear);

            spreadDetails.contract = spreadContract;
            setSelectedSpread(spreadDetails);

            if (spreadContract != null) {
                newSpread = spreadContract.price * spreadDetails.multiplier;
                getSpreadSnapshot(spreadContract, spreadDetails.multiplier);
            }
            else {
                newSpread = null;
            }

            setSelectedFinalPricingDate(isHta
                ? moment(end.expiration).toDate()
                : getSecondLastBusinessDayOfLastMonth(end.expiration));
        }

        setSpread(newSpread);
    }

    function getSpreadSnapshot(contract: FuturesContract, multiplier: number) {
        futuresContractsService.getRealTimeSnapshot(contract.id)
            .then(m => setSpread(m.price * multiplier));
    }

    function changeOrderType(newOrderType: OrderType) {
        setOrderType(newOrderType);

        if (newOrderType === OrderType.Limit) {
            setExpirationInfo({
                type: OrderExpirationType.GoodTilCancel,
                date: null
            })
        }
        else {
            setExpirationInfo(null);
        }
    }

    function calculateLots(rows: ContractRow[]): LotsByPosition[] {
        const byPosition = _.groupBy(rows, m => m.positionSetting.id);

        return _.map(byPosition, g => {
            const ps = positionSettings.find(m => m.id === g[0].positionSetting.id);
            const quantityToSpread = sumArrayBy(g, m => m.rollQuantity ?? m.remainingQuantity);
            return {
                position: ps,
                quantity: quantityToSpread,
                lots: getLotsToHedge(ps, filter.commodity, quantityToSpread, false),
                fcmAccount: fcmAccountsLoader.data?.find(m => m.id === ps?.fcmAccount?.id) ?? null
            }
        });
    }

    async function onFilterChanged(change: Partial<ContractFilter>) {
        let refreshContracts = false;
        if (change.commodity != null && change.commodity.id !== filter.commodity.id) {
            populateCommodityData(change.commodity);
            refreshContracts = true;
        }
        if (change.customerWillSell !== undefined && change.customerWillSell !== filter.customerWillSell) {
            refreshContracts = true;
        }
        const newFilter = { ...filter, ...change };
        if (refreshContracts) {
            populateContracts(newFilter.commodity, newFilter.customerWillSell);
        }
        setFilter(newFilter);
    }

    function selectRows(rows: ContractRow[], isSelected: boolean, deselectOthers: boolean) {
        const rowIds = new Set(rows.map(m => m.id));
        const newVal = allContracts.map(m => ({
            ...m,
            isSelected: rowIds.has(m.id)
                ? isSelected
                : (deselectOthers ? false : m.isSelected)
        }))
        setAllContracts(newVal);

        setLotsByPosition(calculateLots(newVal.filter(m => m.isSelected)));
    }

    function onRowSelected(row: ContractRow, isSelected: boolean) {
        selectRows([row], isSelected, false);
    }

    function onRollQuantityChanged(row: ContractRow, rollQuantity: number) {
        const newVal = allContracts.map(m =>
            m.id === row.id ? { ...m, rollQuantity } : m
        );
        setAllContracts(newVal);
        setLotsByPosition(calculateLots(newVal.filter(m => m.isSelected)));
    }

    function handleLotsChanged(positionSettingId: string, lots: number) {
        setLotsByPosition(all => all.map(m => m.position.id === positionSettingId ? { ...m, lots } : m));
    }

    function handleFcmAccountChanged(positionSettingId: string, fcmAccount: FcmAccount) {
        setLotsByPosition(all => all.map(m => m.position.id === positionSettingId ? { ...m, fcmAccount } : m));
    }

    const selectedContracts = filteredContracts.filter(m => m.isSelected);
    async function onSave() {
        setErrors([]);
        const request: RollContractsRequest = {
            contractType: props.contractType,
            rollFromFuturesContractId: rollFrom.futuresContract.id,
            rollToDeliveryPeriodId: rollTo.deliveryPeriod.id,
            rollToFuturesContractId: rollTo.futuresContract.id,
            rollFee: rollFeeValue,
            spread,
            orderType,
            orderExpirationType: expirationInfo?.type,
            orderExpirationDate: expirationInfo?.date,
            finalPricingDate: formatUTCDate(selectedFinalPricingDate, "yyyy-MM-DD"),
            positionHedges: lotsByPosition.map(m => ({ positionSettingId: m.position.id, lots: m.lots, fcmAccountId: m.fcmAccount?.id ?? null })),
            useUnappliedBalance: useUnappliedBalance,
            contractRollQuantities: selectedContracts.map(m => ({
                contractId: m.id,
                rollQuantity: m.rollQuantity ?? m.remainingQuantity
            }))
        };
        try {
            await contractService.rollContracts(request);
            props.onContractsRolled?.()
            props.onClose();
        }
        catch (err) {
            loggingService.error("Error rolling contracts", err as Error);
            setErrors((err as ValidationError).errors ?? ["An unknown error occurred"]);
        }
    }

    async function onRefreshSpread() {
        getSpreadSnapshot(selectedSpread.contract, selectedSpread.multiplier);
    }

    function onClose() {
        props.onClose();
    }

    function isLoaded(): boolean {
        return commodities.length > 0 && fcmAccountsLoader.isLoaded;
    }

    if (!isLoaded()) return <></>;

    return <JourneyModal className="roll-contracts-modal" size="xl"
        title={`Roll ${getContractTypeName(props.contractType)} Contracts`}
        journey={journey}
        onCancel={onClose}
        isAdvanceButtonDisabled={!filteredContracts.some(m => m.isSelected)}
        advanceButtonText="Roll Contracts"
        async>

        {journey.isShowingDetails && <FormValidation ref={formValidator}>
            <div className="form-inline">
                <Form.Group>
                    <Form.Label>Commodity</Form.Label>
                    <TypedSelect disabled={isSingleContract}
                        options={commodities}
                        value={filter.commodity}
                        valueGetter={m => m.id}
                        textGetter={m => m.displayName}
                        onValueSelected={m => onFilterChanged({ commodity: m })} />
                </Form.Group>

                <Form.Group>
                    <Form.Label>{customerWillSellText.label}</Form.Label>
                    <TypedSelect disabled={isSingleContract}
                        options={[true, false]}
                        value={filter.customerWillSell}
                        valueGetter={m => m}
                        textGetter={customerWillSellText.formatValue}
                        showNullRow={!isHta}
                        nullRowText="Both"
                        //Use ?? below to correct "undefined" to "null"
                        onValueSelected={m => onFilterChanged({ customerWillSell: m ?? null })} />
                </Form.Group>
            </div>

            <div className="form-inline mt-2">
                <Form.Group>
                    <Form.Label>Roll From</Form.Label>
                    <TypedSelect disabled={isSingleContract}
                        required
                        options={rollFromDeliveryPeriods}
                        value={rollFrom.deliveryPeriod}
                        valueGetter={m => m.id}
                        textGetter={getDeliveryPeriodDisplayName}
                        showNullRow
                        nullRowText="Select"
                        onValueSelected={dp => onRollFromChanged({ deliveryPeriod: dp, futuresContract: isHta ? dp.contract : null })} />
                </Form.Group>

                <Form.Group>
                    <Form.Label>Roll From Symbol</Form.Label>
                    <TypedSelect disabled={isSingleContract}
                        required
                        options={_.uniqBy(getVisibleRows(allContracts, rollFrom.deliveryPeriod).map(m => m.futuresContract).filter(m => m != null), m => m.id)}
                        value={rollFrom.futuresContract}
                        valueGetter={m => m.id}
                        textGetter={m => m.symbol}
                        showNullRow={!isHta}
                        nullRowText="All"
                        onValueSelected={fc => onRollFromChanged({ futuresContract: fc })} />
                </Form.Group>

            </div>

            <div className="form-inline mt-2">
                <Form.Group>
                    <Form.Label>Roll To</Form.Label>
                    <TypedSelect
                        required
                        options={deliveryPeriods}
                        value={rollTo.deliveryPeriod}
                        valueGetter={m => m.id}
                        textGetter={getDeliveryPeriodDisplayName}
                        showNullRow
                        nullRowText="Select"
                        onValueSelected={dp => onRollToChanged({ deliveryPeriod: dp, futuresContract: dp.contract })} />
                </Form.Group>

                <Form.Group>
                    <Form.Label>Roll To Symbol</Form.Label>
                    <TypedSelect
                        required
                        options={rollToFuturesContracts}
                        value={rollTo.futuresContract}
                        valueGetter={m => m.id}
                        textGetter={m => m.symbol}
                        showNullRow
                        nullRowText="Select"
                        onValueSelected={fc => onRollToChanged({ futuresContract: fc })} />
                </Form.Group>

                {props.contract != null && activeMerchant.erpSystem === ErpSystem.AgrisDataHub &&
                    <Form.Group>
                        <Form.Label>Use Unapplied Balance</Form.Label>
                        <Form.Check
                            checked={useUnappliedBalance ?? false}
                            onChange={ev => setUseUnappliedBalance(ev.target.checked)}
                        />
                    </Form.Group>
                }
            </div>

            {isHta && <div className="form-inline mt-2">
                <Form.Group>
                    <Form.Label>Pricing Type</Form.Label>
                    <TypedSelect
                        options={[OrderType.Market, OrderType.Limit, OrderType.ManualEntry]}
                        value={orderType}
                        valueGetter={m => m}
                        textGetter={getOfferContractOrderTypeName}
                        onValueSelected={m => changeOrderType(m)} />
                </Form.Group>

                {orderType === OrderType.Limit &&
                    <Form.Group>
                        <Form.Label column>Expiration</Form.Label>
                        <TypedSelect
                            options={AllOrderExpirationTypes}
                            value={expirationInfo?.type}
                            valueGetter={m => m}
                            textGetter={m => getOrderExpirationTypeName(m)}
                            onValueSelected={m => setExpirationInfo(x => ({ ...x, type: m }))} />
                    </Form.Group>
                }

                {expirationInfo?.type === OrderExpirationType.GoodTilDate &&
                    <Form.Group>
                        <Form.Label>Date</Form.Label>
                        <Form.Control type="date"
                            value={toDateString(expirationInfo?.date)}
                            required
                            max={toDateString(getPreviousDayForGivenDate(rollTo.futuresContract?.expiration))}
                            min={toDateString(getNextDay())}
                            onChange={ev => setExpirationInfo(x => ({ ...x, date: moment(ev.target.value).toDate() }))}>
                        </Form.Control>
                    </Form.Group>
                }
            </div>}

            <div className="form-inline mt-2">

                <Form.Group>
                    <Form.Label>Final Pricing Date</Form.Label>
                    <Form.Control type="date"
                        disabled={rollTo.futuresContract == null}
                        value={formatUTCDate(selectedFinalPricingDate, "yyyy-MM-DD")}
                        required
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setSelectedFinalPricingDate(moment(ev.target.value).toDate())}>
                    </Form.Control>
                </Form.Group>

                <Form.Group>
                    <Form.Label>Spread</Form.Label>
                    <NumberEditor
                        required
                        placeholder="--"
                        value={spread}
                        onValueChange={m => setSpread(m ?? 0)}
                        step={.01}
                        precision={5}
                        enforcePrecision
                        allowDecimals />
                    {selectedSpread.contract != null && orderType !== OrderType.ManualEntry &&
                        <button type="button" className="link-button"
                            onClick={onRefreshSpread}
                            title="Refresh spread price">
                            <i className="material-icons">refresh</i>
                        </button>
                    }
                </Form.Group>

                <Form.Group>
                    <Form.Label>Roll Fee</Form.Label>
                    <NumberEditor
                        value={rollFeeValue}
                        onValueChange={m => setRollFeeValue(m ?? 0)}
                        min={0}
                        step={.01}
                        disabled={rollTo.futuresContract == null || isMissingRollFee}
                        precision={4}
                        enforcePrecision
                        allowDecimals />
                </Form.Group>
            </div>

            <RollTable as={isHta ? RollHtaTable : RollBasisTable}
                rows={filteredContracts}
                positions={positionSettings}
                rollTo={rollTo}
                onRowSelected={onRowSelected}
                onSelectAll={selectAll}
                onRollQuantityChanged={onRollQuantityChanged}
                hideRowSelection={isSingleContract}
                spread={spread}
                showErp={hasErpIntegration}
                allowPartialRoll={allowPartialRoll} />

            {isHta && <UnhedgedContracts
                commodity={filter.commodity}
                lotsByPosition={lotsByPosition}
                fcmAccounts={fcmAccountsLoader.data}
                onLotsChanged={handleLotsChanged}
                onFcmAccountChanged={handleFcmAccountChanged}
            />}
        </FormValidation>}
        {journey.isShowingConfirmation && <Confirmation
            type={props.contractType}
            commodity={filter.commodity}
            rollFrom={rollFrom}
            rollTo={rollTo}
            rollFee={rollFeeValue}
            spread={spread}
            finalPricingDate={selectedFinalPricingDate}
            contracts={selectedContracts}
            positionHedges={lotsByPosition}
            units={unitConversionRates}
            currency={selectedContracts[0].contractCurrency}
            uiCustomization={uiCustomization} />}
        <ErrorList errors={errors} />
    </JourneyModal>;
}

function toDateString(dt: Date): string {
    return formatUTCDate(dt, "yyyy-MM-DD");
}

interface ConfirmationProps {
    type: ContractType;
    commodity: Commodity;
    rollFrom: RollParameters;
    rollTo: RollParameters;
    rollFee: number;
    spread: number;
    finalPricingDate: Date;
    contracts: ContractRow[];
    positionHedges: LotsByPosition[];

    units: UnitConversionRate[];
    currency: Currency;
    uiCustomization: UiCustomization;
}

const Confirmation: React.FC<ConfirmationProps> = React.memo(props => {
    const isHta = contractTypeHelper.isHta(props.type);
    const showLocalUOM = props.commodity.baseUnit.id !== props.commodity.localUnit.id;
    const quantityPrecision = props.uiCustomization.addEditOfferContractQuantityDecimalPrecision;

    function formatMoney(value: number): string {
        return unitHelper.getPriceText(value, showLocalUOM, props.currency.code, props.commodity, props.units)
    }

    const totalRemainingQuantity = _.sumBy(props.contracts, m => m.rollQuantity ?? m.remainingQuantity);

    return <div className="roll-contracts-confirmation">

        <DetailedList>
            <DetailRow label="Contracts" value={<span>
                {pluralize(props.contracts.length, "contract")} totaling {unitHelper.getQuantityTextWithUnits(totalRemainingQuantity, props.commodity, props.units, quantityPrecision)}
            </span>} highlight />
            <DetailRow label="Roll From" value={getDeliveryPeriodDisplayName(props.rollFrom.deliveryPeriod)} highlight />
            <DetailRow label="Roll To" value={getDeliveryPeriodDisplayName(props.rollTo.deliveryPeriod)} highlight />
            <DetailRow label={isHta ? "Traded Spread" : "Spread"} value={formatMoney(props.spread)} />
            <DetailRow label="Roll Fee" value={formatMoney(props.rollFee)} />
            <DetailRow label="Final Pricing Date" value={formatUTCDate(props.finalPricingDate, "yyyy-MM-DD")} />
            {isHta && props.positionHedges.length > 0 && <DetailRow label="Additional Hedges" value={<span>
                {props.positionHedges.map(m => <div>{getPositionDisplayName(m.position)} - {m.lots}</div>)}
            </span>} />}
        </DetailedList>
    </div>;
})

const RollTable: React.FC<RollTableProps & { as: any }> = React.memo(props => {
    const As = props.as;
    return <As {...props} />;
})

interface UnhedgedContractsProps {
    commodity: Commodity;
    lotsByPosition: LotsByPosition[];
    fcmAccounts: FcmAccount[];
    onLotsChanged(positionId: string, lots: number): void;
    onFcmAccountChanged(positionId: string, fcmAccount: FcmAccount): void;
}

type LotsByPosition = { position: PositionSetting, quantity: number, lots: number, fcmAccount: FcmAccount | null };

const UnhedgedContracts: React.FC<UnhedgedContractsProps> = React.memo(props => {

    return <div className="unhedged-contracts">
        <h2>Hedge Result</h2>
        <StandardGrid rowData={props.lotsByPosition}
            suppressRowClickSelection
            rowHeight={28}
            getRowId={getRowId}>
            <AgGridColumn headerName="Position"
                valueGetter={((p: ValueGetterParams) => getPositionDisplayName(p.data.position))} />
            <AgGridColumn headerName="FCM Account"
                width={160}
                cellClass="editable-cell"
                valueGetter={(p: ValueGetterParams) => p.data.fcmAccount}
                cellRenderer={(p: RenderParameters<LotsByPosition>) => <TypedSelect
                    options={props.fcmAccounts}
                    value={p.value}
                    required
                    showNullRow
                    nullRowText="Select"
                    valueGetter={m => m.id}
                    textGetter={m => m.accountNumber}
                    onValueSelected={m => props.onFcmAccountChanged(p.node.data.position.id, m)}
                />} />
            <AgGridColumn field="quantity" headerName="Quantity to Spread"
                valueFormatter={(p: ValueFormatterParams) => formatDecimalPlace(p.value)} />
            <AgGridColumn field="lots" headerName="# of Contracts"
                width={120}
                cellClass="editable-cell"
                cellRenderer={(p: RenderParameters<LotsByPosition, LotsByPosition['lots']>) => <NumberEditor
                    value={p.value}
                    step={1}
                    min={0}
                    onValueChange={m => props.onLotsChanged(p.node.data.position.id, m)}
                />}
            />
            <AgGridColumn headerName="Hedged"
                valueGetter={(params: ValueGetterParams) => params.data.lots * props.commodity.contractSize}
            />
        </StandardGrid>
    </div>;
});

const getRowId = (params: any) => params.data.position.id;

function getPositionDisplayName(position: PositionSetting): string {
    return getPositionNameForPositionGroup(position.positionGroup, position.displayName);
}

export default RollContractsModal;

